/**
 * Top 100 most common passwords to block
 * Prevents weak password selection during signup
 */
export const COMMON_PASSWORDS = [
  '123456',
  'password',
  '12345678',
  'qwerty',
  '123456789',
  '12345',
  '1234',
  '111111',
  '1234567',
  'dragon',
  '123123',
  'baseball',
  'iloveyou',
  'trustno1',
  '1234567890',
  'sunshine',
  'master',
  'welcome',
  'shadow',
  'ashley',
  'football',
  'jesus',
  'michael',
  'ninja',
  'mustang',
  'password1',
  'Password1',
  'Password123',
  'admin',
  'admin123',
  'root',
  'toor',
  'pass',
  'test',
  'guest',
  'info',
  'adm',
  'mysql',
  'user',
  'administrator',
  'oracle',
  'ftp',
  'pi',
  'puppet',
  'ansible',
  'ec2-user',
  'vagrant',
  'azureuser',
  'academy',
  'acceptance',
  'access',
  'account',
  'accounting',
  'alpine',
  'anon',
  'anonymous',
  'api',
  'backup',
  'batch',
  'beta',
  'build',
  'builder',
  'business',
  'charlie',
  'client',
  'cloud',
  'config',
  'connect',
  'contact',
  'data',
  'database',
  'db',
  'debug',
  'default',
  'demo',
  'deploy',
  'dev',
  'developer',
  'development',
  'docker',
  'download',
  'email',
  'example',
  'exchange',
  'file',
  'files',
  'finance',
  'forum',
  'ftp',
  'git',
  'github',
  'gitlab',
  'global',
  'help',
  'home',
  'host',
  'hosting',
  'hr',
  'image',
  'images',
];

/**
 * Check if password contains sequential patterns
 */
export function hasSequentialPattern(password: string): boolean {
  const lower = password.toLowerCase();
  
  // Check for sequential numbers
  const numSequences = ['0123', '1234', '2345', '3456', '4567', '5678', '6789'];
  if (numSequences.some(seq => lower.includes(seq))) return true;
  
  // Check for sequential letters
  const letterSequences = ['abcd', 'bcde', 'cdef', 'defg', 'efgh', 'fghi', 'ghij'];
  if (letterSequences.some(seq => lower.includes(seq))) return true;
  
  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) return true;
  
  return false;
}

/**
 * Comprehensive password validation
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  errors: string[];
  score: number;
} {
  const errors: string[] = [];
  let score = 0;

  // Length check
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  } else {
    score += 2;
  }

  // Character type checks
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain lowercase letters');
  } else {
    score += 1;
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain uppercase letters');
  } else {
    score += 1;
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain numbers');
  } else {
    score += 1;
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain special characters (!@#$%^&*...)');
  } else {
    score += 1;
  }

  // Common password check
  const lowerPassword = password.toLowerCase();
  if (COMMON_PASSWORDS.some(common => lowerPassword.includes(common.toLowerCase()))) {
    errors.push('Password is too common or contains common words');
    score -= 2;
  }

  // Sequential pattern check
  if (hasSequentialPattern(password)) {
    errors.push('Password contains sequential patterns (123, abc, etc.)');
    score -= 1;
  }

  return {
    isValid: errors.length === 0,
    errors,
    score: Math.max(0, score)
  };
}
